/**
 * SUB-4.4 · FR38, design A1. The owning account of an open record, shown
 * persistently and prominently at every viewport width.
 *
 * ⚠️ IT IS A BAR AND NOT A CHIP, AND THAT IS THE REQUIREMENT RATHER THAN A
 * PREFERENCE. FR38 says persistent at EVERY viewport width. A chip in the title
 * row shares its line with a project name, an edit control and a share control,
 * so at 380px it either wraps the title or truncates itself — and a truncated
 * account name is worse than none, because "Acme Sol…" and "Acme Solar East" are
 * two different clients. A full-width row above the title cannot be squeezed by
 * its neighbours because it has none.
 *
 * ⚠️ IT MUST BE STICKY WITH THE HEADER, NOT MERELY ABOVE IT. "Persistent" is
 * about scrolling as much as about width: a record modal is several screens
 * long and the attribution has to still be on screen when the user is editing a
 * field near the bottom. It is rendered inside the header's sticky wrapper.
 *
 * ⚠️ COLOUR IS NOT THE CARRIER. Same rule as `AccountColumn.tsx`: D21 approved
 * a colour rail only as a redundant cue, colour alone fails WCAG 1.4.1, and the
 * customer runs thirteen accounts against about five distinguishable hues. The
 * account is text and an icon; the tint is decoration on top of both.
 */

import { Building2 } from 'lucide-react';
import type { RecordAccountBarState } from '@/lib/recordAccountBar';

export function RecordAccountBar({ state }: { state: RecordAccountBarState }) {
  if (!state.visible) return null;

  const { label, isOtherAccount, announcement } = state;

  return (
    <div
      // `role="status"` rather than a bare div: the bar appears when a record
      // from another account is opened, and a sighted user gets that from the
      // tint. Without this a screen-reader user gets it from nothing.
      role="status"
      aria-live="polite"
      className={[
        'flex w-full items-center gap-2 border-b px-4 py-2 text-sm',
        // min-w-0 so the flex child may shrink; the NAME truncates before the
        // label does, and only after the row has run out of width entirely.
        'min-w-0',
        isOtherAccount
          ? 'border-primary/30 bg-primary/10 text-foreground'
          : 'border-border bg-muted/40 text-muted-foreground',
      ].join(' ')}
    >
      <Building2
        className={`h-4 w-4 flex-shrink-0 ${isOtherAccount ? 'text-primary' : ''}`}
        aria-hidden="true"
      />
      <span className="flex-shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        Account
      </span>
      <span
        className={[
          'min-w-0 truncate font-medium',
          label.known ? '' : 'italic text-muted-foreground',
        ].join(' ')}
        title={label.known ? label.name : `Account id ${label.tenantId || 'missing'}`}
      >
        {label.name}
      </span>
      {/* The full sentence, for assistive technology only. The visible row is
          two words and a name; the announcement says what that MEANS, which is
          the part the tint carries for everyone else. */}
      <span className="sr-only">{announcement}</span>
    </div>
  );
}

export default RecordAccountBar;
